/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * A cursor over a FIT byte array.
 *
 * The whole file is held in memory: FIT records are variable-length and the
 * decoder needs to seek across chained files, so streaming buys little. A [Crc]
 * can be attached to [crc] to checksum bytes as they are consumed, which is how
 * the decoder verifies a file without a second pass.
 */
public class ByteReader(
    private val bytes: ByteArray,
    position: Int = 0,
) {
    public var position: Int = position
        private set

    /** Attached checksum, updated by every read. Null disables checksumming. */
    public var crc: Crc? = null

    public val size: Int get() = bytes.size

    public val bytesAvailable: Int get() = bytes.size - position

    public val hasBytesAvailable: Boolean get() = position < bytes.size

    public operator fun get(index: Int): UByte = bytes[index].toUByte()

    public fun reset() {
        position = 0
        crc?.reset()
    }

    /**
     * Moves the cursor to [position].
     *
     * An out-of-range target is a [FitFormatException] rather than an
     * [IllegalArgumentException]: every seek here is driven by lengths read out
     * of the file, so a bad one means bad input, and the decoder's promise never
     * to let anything but a [FitException] escape depends on it.
     */
    public fun seek(position: Int) {
        if (position < 0 || position > bytes.size) {
            throw FitFormatException("tried to seek to $position, outside 0..${bytes.size}", this.position)
        }
        this.position = position
    }

    public fun skip(count: Int) {
        if (count < 0) throw FitFormatException("tried to skip a negative count: $count", position)
        val end = position + count
        if (end > bytes.size) throw FitFormatException("tried to skip $count bytes with only $bytesAvailable remaining", position)
        crc?.update(bytes, position, end)
        position = end
    }

    /** Reads the next byte without consuming it. Returns 0 at end of input. */
    public fun peekByte(): UByte = if (hasBytesAvailable) bytes[position].toUByte() else 0u

    public fun readByte(): UByte {
        if (!hasBytesAvailable) throw FitFormatException("unexpected end of input", position)
        val b = bytes[position]
        crc?.update(b)
        position++
        return b.toUByte()
    }

    public fun readBytes(count: Int): ByteArray {
        if (count < 0) throw FitFormatException("tried to read a negative count: $count", position)
        if (count > bytesAvailable) throw FitFormatException("tried to read $count bytes with only $bytesAvailable remaining", position)
        val slice = bytes.copyOfRange(position, position + count)
        crc?.update(bytes, position, position + count)
        position += count
        return slice
    }

    /** Copies a range without moving the cursor or touching the checksum. */
    public fun slice(startIndex: Int, endIndex: Int): ByteArray = bytes.copyOfRange(startIndex, endIndex)

    private fun readUnsigned(byteCount: Int, endianness: Endianness): ULong {
        if (byteCount > bytesAvailable) throw FitFormatException("unexpected end of input", position)
        var acc = 0uL
        if (endianness == Endianness.LITTLE) {
            for (i in byteCount - 1 downTo 0) {
                acc = (acc shl 8) or (bytes[position + i].toULong() and 0xFFuL)
            }
        } else {
            for (i in 0 until byteCount) {
                acc = (acc shl 8) or (bytes[position + i].toULong() and 0xFFuL)
            }
        }
        crc?.update(bytes, position, position + byteCount)
        position += byteCount
        return acc
    }

    public fun readUByte(): UByte = readByte()

    public fun readByteSigned(): Byte = readByte().toByte()

    public fun readUShort(endianness: Endianness = Endianness.LITTLE): UShort = readUnsigned(2, endianness).toUShort()

    public fun readShort(endianness: Endianness = Endianness.LITTLE): Short = readUnsigned(2, endianness).toShort()

    public fun readUInt(endianness: Endianness = Endianness.LITTLE): UInt = readUnsigned(4, endianness).toUInt()

    public fun readInt(endianness: Endianness = Endianness.LITTLE): Int = readUnsigned(4, endianness).toInt()

    public fun readULong(endianness: Endianness = Endianness.LITTLE): ULong = readUnsigned(8, endianness)

    public fun readLong(endianness: Endianness = Endianness.LITTLE): Long = readUnsigned(8, endianness).toLong()

    public fun readFloat(endianness: Endianness = Endianness.LITTLE): Float = Float.fromBits(readUnsigned(4, endianness).toInt())

    public fun readDouble(endianness: Endianness = Endianness.LITTLE): Double = Double.fromBits(readUnsigned(8, endianness).toLong())

    /** Reads [count] bytes and decodes them as UTF-8, dropping the NUL terminator. */
    public fun readString(count: Int): String = readBytes(count).decodeToString().trimEnd('\u0000')
}
